import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NotificationStack,
  NotificationType,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Input,
  Textarea,
} from "@transfr-inc/dashboard-components/forms";

import "./ai-overrides.modal.scss";

const TASK_INSTRUCTIONS_MAX = 50_000;

// Excluded for the moment since submitting criteria does not impact state standards retrieval
const EXCLUDED_KEYS = new Set(["state_standards_top_k"]);

const boundsMessage = (isInt, min, max) => {
  const noun = isInt ? "a whole number" : "a number";
  if (min != null && max != null)
    return `Must be ${noun} from ${min} to ${max}.`;
  if (min != null) return `Must be ${noun} ${min} or greater.`;
  if (max != null) return `Must be ${noun} ${max} or less.`;
  return `Must be ${noun}.`;
};

/**
 * Validates the override map against each tunable's type and bounds. Bounds come
 * from the settings catalog, so
 * the client and server share one source of truth. Returns a { key: message }
 * map, keyed like the backend 422 errors so they share the modal's field-error
 * channel. Blank fields mean "don't override" and are skipped.
 */
export const validateOverrideSettings = (
  overrideSettings = {},
  settings = []
) => {
  const specByKey = Object.fromEntries(settings.map((t) => [t.key, t]));
  const errors = {};
  for (const [key, raw] of Object.entries(overrideSettings)) {
    if (raw == null || String(raw).trim() === "") continue;
    const spec = specByKey[key];
    const type = spec?.type;
    // String tunables (e.g. model) accept any non-blank value.
    if (type !== "int" && type !== "float") continue;
    const n = Number(raw);
    const { minimum: min, maximum: max } = spec;
    const isInt = type === "int";
    const valid =
      Number.isFinite(n) &&
      (!isInt || Number.isInteger(n)) &&
      (min == null || n >= min) &&
      (max == null || n <= max);
    if (!valid) errors[key] = boundsMessage(isInt, min, max);
  }
  return errors;
};

// Hover-tooltip text per tunable, keyed by wire key.
const TUNABLE_DESCRIPTIONS = {
  model: "The Gemini model used to rank and prune recommendations.",
  temperature:
    "Sampling randomness (0–2). Lower is more deterministic, higher is more varied.",
  top_p:
    "Nucleus sampling: the cumulative probability mass to sample from (0–1).",
  top_k: "Limits sampling to the K most likely next tokens.",
  max_returned_items:
    "Maximum number of recommended items the model may return.",
  reasoning_max_chars:
    "Character cap on the model's per-item reasoning explanation.",
  pruned_reasoning_max_chars:
    "Character cap on the reasoning explanation for pruned (non-selected) items.",
  include_reasoning:
    "Whether the model must justify each item. Required for recall — disabling biases toward dropping items.",
  similarity_threshold:
    "Minimum embedding similarity for a candidate to enter the pool (0–1).",
  max_candidates_per_standard:
    "Maximum candidate simulations retrieved per matched standard.",
  max_standards_per_candidate:
    "Maximum standards a single candidate can be credited with.",
  thinking_budget:
    "Token budget for the model's internal reasoning. 0 disables thinking.",
  llm_timeout_ms: "Timeout for the recommender LLM call, in milliseconds.",
  class_time_cap_lt_1h:
    "Max recommended items for classes with under 1 hour of class time per week.",
  class_time_cap_1_3h:
    "Max recommended items for classes with 1–3 hours of class time per week.",
  class_time_cap_3_5h:
    "Max recommended items for classes with 3–5 hours of class time per week.",
  class_time_cap_gt_5h:
    "Max recommended items for classes with 5+ hours of class time per week. Blank = uncapped.",
};

const TUNABLE_GROUPS = [
  {
    title: "Model & Sampling",
    keys: ["model", "temperature", "top_p", "top_k"],
  },
  {
    title: "Retrieval & Candidate Pool",
    keys: [
      "similarity_threshold",
      "max_candidates_per_standard",
      "max_standards_per_candidate",
    ],
  },
  {
    title: "Output & Reasoning",
    keys: [
      "max_returned_items",
      "reasoning_max_chars",
      "pruned_reasoning_max_chars",
      "include_reasoning",
    ],
  },
  {
    title: "Quick Start Output Filters",
    keys: [
      "class_time_cap_lt_1h",
      "class_time_cap_1_3h",
      "class_time_cap_3_5h",
      "class_time_cap_gt_5h",
    ],
  },
  { title: "Operational", keys: ["thinking_budget", "llm_timeout_ms"] },
];

const OTHER_GROUP_TITLE = "Other";

const BOOL_OPTIONS = [
  { id: "", name: "(default)" },
  { id: "true", name: "true" },
  { id: "false", name: "false" },
];

/**
 * Buckets the visible tunables into TUNABLE_GROUPS order, preserving each
 * group's declared key order. Any key not claimed by a group lands in a
 * trailing "Other" group, so unrecognized backend tunables still surface.
 * Empty groups are dropped so a header never renders without fields.
 */
export const groupTunables = (tunables = []) => {
  const byKey = new Map(tunables.map((t) => [t.key, t]));
  const assigned = new Set();
  const groups = TUNABLE_GROUPS.map(({ title, keys }) => {
    const items = keys.map((k) => byKey.get(k)).filter(Boolean);
    for (const item of items) assigned.add(item.key);
    return { title, items };
  });
  const leftovers = tunables.filter((t) => !assigned.has(t.key));
  if (leftovers.length > 0) {
    groups.push({ title: OTHER_GROUP_TITLE, items: leftovers });
  }
  return groups.filter((g) => g.items.length > 0);
};

// Input/Textarea callbacks hand back either a DOM event or a bare value.
const readValue = (eventOrValue) =>
  typeof eventOrValue === "string"
    ? eventOrValue
    : eventOrValue?.target?.value ?? "";

// Casts a raw input string to the tunable's declared type so the backend gets
// numbers, not strings. Uses Number() (not parseInt/parseFloat) so partially
// numeric input like "1abc" becomes NaN and passes through unchanged for the
// server to 422, rather than being silently truncated to 1.
export const castTunableValue = (type, raw) => {
  if (type === "int" || type === "float") {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }

  if (type === "bool") return raw === "true" || raw === true;
  return raw;
};

/**
 * "Advanced settings" pop-up for per-call recommender overrides (gated on the
 * settings-editing flag via `enabled`). Controlled by the parent wizard:
 * `overrideSettings` holds only the touched fields as raw strings, and
 * `onSettingChange(key, value)` is called with `undefined` to clear a field.
 */
export const AiOverridesModal = ({
  enabled,
  open = false,
  onClose,
  settings = [],
  loading = false,
  overrideSettings = {},
  taskInstructions = "",
  fieldErrors = {},
  onSettingChange,
  onTaskInstructionsChange,
}) => {
  if (!enabled) return null;

  // Live client-side validation merged with backend 422s (backend wins).
  const errors = {
    ...validateOverrideSettings(overrideSettings, settings),
    ...fieldErrors,
  };

  const fieldKeys = new Set(
    settings.filter((t) => !EXCLUDED_KEYS.has(t.key)).map((t) => t.key)
  );
  const inputNotifications = Object.entries(errors)
    .filter(([key]) => !fieldKeys.has(key))
    .map(([key, message]) => ({
      type: NotificationType.error,
      message: `${key}: ${message}`,
    }));

  const renderField = (tunable) => {
    const {
      key,
      type,
      default: defaultValue,
      value,
      minimum,
      maximum,
    } = tunable;
    const baseline = value ?? defaultValue;
    const message = errors[key];
    const description = TUNABLE_DESCRIPTIONS[key];
    return (
      <div key={key} className="ai-overrides-modal__field">
        <label className="ai-overrides-modal__label" htmlFor={key}>
          {key}
          {baseline != null && (
            <span className="ai-overrides-modal__default">
              {" "}
              (default: {String(baseline)})
            </span>
          )}
          {description && (
            <TextTooltip text={description}>
              <FontAwesomeIcon
                className="ai-overrides-modal__info"
                icon="fa-regular fa-circle-info"
              />
            </TextTooltip>
          )}
        </label>
        <span className="ai-overrides-modal__error">{message}</span>
        {type === "bool" ? (
          (() => {
            const selectedOption =
              BOOL_OPTIONS.find(
                (o) => o.id === (overrideSettings[key] ?? "")
              ) ?? BOOL_OPTIONS[0];
            return (
              <Dropdown
                selected={selectedOption}
                onOptionSelected={(option) =>
                  onSettingChange?.(
                    key,
                    option.id === "" ? undefined : option.id
                  )
                }
              >
                <DropdownButton id={key} placeholder="(default)">
                  {selectedOption?.name}
                </DropdownButton>
                <DropdownItems>
                  {BOOL_OPTIONS.map((option) => (
                    <DropdownItem key={option.id} value={option}>
                      {option.name}
                    </DropdownItem>
                  ))}
                </DropdownItems>
              </Dropdown>
            );
          })()
        ) : (
          <Input
            id={key}
            name={key}
            type={type === "str" ? "text" : "number"}
            step={type === "float" ? "any" : undefined}
            min={minimum ?? undefined}
            max={maximum ?? undefined}
            value={overrideSettings[key] ?? ""}
            onChange={(e) => {
              const next = readValue(e);
              onSettingChange?.(key, next === "" ? undefined : next);
            }}
          />
        )}
      </div>
    );
  };

  const groups = groupTunables(
    settings.filter((tunable) => !EXCLUDED_KEYS.has(tunable.key))
  );

  return (
    <Modal open={open} modalClassName="ai-overrides-modal" onClose={onClose}>
      <ModalHeader
        className="blue-icon"
        icon={["fa-regular", "sliders"]}
        title="Advanced Settings"
      />
      <ModalBody>
        {inputNotifications.length > 0 && (
          <NotificationStack notifications={inputNotifications} />
        )}
        <p className="ai-overrides-modal__hint">
          Sandbox/Dev-only overrides applied to this run only. Leave a field
          blank to use its stored or default value.
        </p>
        <div className="ai-overrides-modal__form">
          <div className="ai-overrides-modal__field">
            <label
              className="ai-overrides-modal__label"
              htmlFor="taskInstructions"
            >
              Task instructions
            </label>
            <Textarea
              id="taskInstructions"
              name="taskInstructions"
              rows={5}
              maxLength={TASK_INSTRUCTIONS_MAX}
              value={taskInstructions}
              onInputChange={(e) => onTaskInstructionsChange?.(readValue(e))}
            />
          </div>

          {loading && (
            <p className="ai-overrides-modal__loading">Loading settings…</p>
          )}

          {groups.map((group) => (
            <div key={group.title} className="ai-overrides-modal__group">
              <h3 className="ai-overrides-modal__group-title">{group.title}</h3>
              {group.items.map((tunable) => renderField(tunable))}
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button primary onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};
